// src/pages/languages/LanguagesList.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Globe2,
  Edit2,
  Trash2,
  X,
  FolderOpen,
  BookOpen,
  Image as ImageIcon,
} from "lucide-react";
import { languagesAPI } from "../../services/languageService";

/* ─── Create / Edit Modal ─── */
function LanguageFormModal({ language, onClose, onSaved }) {
  const isEdit = Boolean(language);
  const [form, setForm] = useState({
    code: language?.code || "",
    name: language?.name || "",
    native_name: language?.native_name || "",
    description: language?.description || "",
    order: language?.order ?? 0,
    is_active: language?.is_active ?? true,
  });
  const [flagFile, setFlagFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, system_type: "UNIFIED" };
      if (flagFile) payload.flag = flagFile;

      if (isEdit) {
        await languagesAPI.update(language.id, payload);
      } else {
        await languagesAPI.create(payload);
      }
      onSaved();
    } catch (err) {
      setError(
        err?.response?.data
          ? JSON.stringify(err.response.data)
          : "حدث خطأ أثناء الحفظ"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">
            {isEdit ? "تعديل اللغة" : "إضافة لغة جديدة"}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                الكود (code)
              </label>
              <input
                type="text"
                required
                placeholder="fr"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                الترتيب
              </label>
              <input
                type="number"
                value={form.order}
                onChange={(e) =>
                  setForm({ ...form, order: Number(e.target.value) })
                }
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              الاسم بالإنجليزي
            </label>
            <input
              type="text"
              required
              placeholder="French"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              الاسم الأصلي (native name)
            </label>
            <input
              type="text"
              placeholder="Français"
              value={form.native_name}
              onChange={(e) =>
                setForm({ ...form, native_name: e.target.value })
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              الوصف
            </label>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              علم اللغة (Flag)
            </label>
            <label className="flex items-center gap-2 border border-dashed border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-500 cursor-pointer hover:bg-gray-50">
              <ImageIcon className="w-4 h-4" />
              {flagFile ? flagFile.name : "اختر صورة"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setFlagFile(e.target.files?.[0] || null)}
              />
            </label>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) =>
                setForm({ ...form, is_active: e.target.checked })
              }
            />
            مفعّلة
          </label>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-60"
            >
              {saving ? "جارٍ الحفظ..." : "حفظ"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export default function LanguagesList() {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState(null);

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      setLoading(true);
      const data = await languagesAPI.getAll();
      setLanguages(data.languages || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (lang) => {
    if (lang.system_type === "LEGACY") return;
    if (!window.confirm(`هل أنت متأكد من حذف لغة "${lang.name}"؟`)) return;
    try {
      await languagesAPI.delete(lang.id);
      setLanguages((prev) => prev.filter((l) => l.id !== lang.id));
    } catch {
      alert("حدث خطأ أثناء الحذف");
    }
  };

  const openCreate = () => {
    setEditingLanguage(null);
    setModalOpen(true);
  };

  const openEdit = (lang) => {
    setEditingLanguage(lang);
    setModalOpen(true);
  };

  const handleSaved = () => {
    setModalOpen(false);
    fetchLanguages();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">اللغات</h1>
          <p className="text-gray-500 text-sm mt-1">
            إدارة اللغات المتاحة على المنصة
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>إضافة لغة</span>
        </button>
      </div>

      {languages.length === 0 ? (
        <div className="card text-center py-16">
          <Globe2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-2">لا توجد لغات</h3>
          <p className="text-gray-500 text-sm mb-6">ابدأ بإضافة لغة جديدة</p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            إضافة لغة
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {languages.map((lang) => (
            <div
              key={lang.id}
              className="card hover:shadow-lg transition-shadow"
            >
              <div className="bg-blue-50 rounded-xl p-4 mb-4 flex items-center gap-3">
                {lang.flag_url ? (
                  <img
                    src={lang.flag_url}
                    alt={lang.name}
                    className="w-10 h-10 rounded-full object-cover border border-white shadow"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Globe2 className="w-5 h-5 text-blue-600" />
                  </div>
                )}
                <div>
                  <p className="font-bold text-gray-900">{lang.name}</p>
                  {lang.native_name && (
                    <p className="text-xs font-medium text-blue-600">
                      {lang.native_name}
                    </p>
                  )}
                </div>
              </div>

              {lang.description && (
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {lang.description}
                </p>
              )}

              <div className="flex items-center justify-between text-sm mb-4">
                <span className="text-gray-500 flex items-center gap-1">
                  <FolderOpen className="w-3.5 h-3.5" />
                  {lang.total_categories ?? 0} كاتيجوري
                </span>
                <span className="font-bold text-blue-600 flex items-center gap-1">
                  <BookOpen className="w-3.5 h-3.5" />
                  {lang.total_questions ?? 0}
                </span>
              </div>

              <div className="flex gap-2">
                <Link
                  to={`/dashboard/languages/${lang.id}/categories`}
                  className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm transition-colors"
                >
                  عرض الكاتيجوريز
                </Link>
                <button
                  onClick={() => openEdit(lang)}
                  className="flex items-center justify-center p-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                {lang.system_type !== "LEGACY" && (
                  <button
                    onClick={() => handleDelete(lang)}
                    className="flex items-center justify-center p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <LanguageFormModal
          language={editingLanguage}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}